import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiShoppingCart,
  FiUser,
  FiSearch,
  FiHeart,
  FiMenu,
  FiX,
  FiLogOut,
  FiShoppingBag,
} from 'react-icons/fi';
import '../styles/Header.css';

// Import your logo - make sure to update the path to your actual logo file
import logo from '../assets/image/Fahple-logo.jpg';

const Header = () => {
  const [currentPage, setCurrentPage] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false); // Simulate login state
  const location = useLocation();
  const navigate = useNavigate();

  // Pages where search bar should be visible
  const searchBarVisiblePages = [
    '/about',
    '/products',
    '/service',
    '/job',
    '/contact',
  ];

  // Pages where search bar should be hidden
  const searchBarHiddenPages = [
    '/login',
    '/signup',
    '/profile',
    '/cart',
    '/checkout',
    '/orders',
    '/admin',
    '/admin-dashboard',
  ];

  const shouldShowSearchBar = () => {
    const currentPath = location.pathname;

    // First check if we're on a page where search bar should be hidden
    if (searchBarHiddenPages.some((path) => currentPath.startsWith(path))) {
      return false;
    }

    // Then check if we're on a page where search bar should be visible
    if (searchBarVisiblePages.some((path) => currentPath.startsWith(path))) {
      return true;
    }

    // For all other pages (like home), don't show search bar
    return false;
  };

  useEffect(() => {
    // Get current page from pathname
    const path = location.pathname.split('/').pop() || 'index';
    setCurrentPage(path);

    // Simulate checking if user is logged in (replace with actual auth check)
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
  }, [location]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleDropdownToggle = (dropdownName, e) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const handleNavigation = (path) => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    navigate(path);
  };

  const handleLanguageChange = (langCode) => {
    console.log(`Changing language to: ${langCode}`);
    alert(`Language changed to ${langCode.toUpperCase()}`);
    // Implement language change logic here
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const handleLogin = () => {
   window.location.href = 'https://admin.fahple.com';
  };

  const handleSignup = () => {
    window.location.href = 'https://admin.fahple.com';
  };

  const handleLogout = () => {
    // Simulate logout
    localStorage.removeItem('authToken');
    setIsLoggedIn(false);
    setActiveDropdown(null);
    navigate('/');
    alert('Logged out successfully');
  };

  const handleProfileNavigation = (path) => {
    setActiveDropdown(null);
    handleNavigation(path);
  };

  const isActive = (pageName) => {
    return currentPage === pageName ? 'active' : '';
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Reset mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 991) {
        setIsMobileMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <header>
      <nav className='navbar'>
        {/* Logo Section */}
        <div className='logo-section'>
          <img
            src={logo}
            alt='Fahple Logo'
            className='logo'
            onClick={() => handleNavigation('/')}
          />
        </div>

        {/* Mobile Menu Toggle */}
        <div
          className={`menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
          id='menuToggle'
          onClick={handleMobileMenuToggle}
        >
          {isMobileMenuOpen ? <FiX /> : <FiMenu />}
        </div>

        {/* Navigation Links */}
        <ul
          className={`nav-links ${isMobileMenuOpen ? 'active' : ''}`}
          id='navLinks'
        >
          <li>
            <a
              href='/'
              className={
                isActive('index') || currentPage === '' ? 'active' : ''
              }
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/');
              }}
            >
              Home
            </a>
          </li>
          <li>
            <a
              href='/about'
              className={isActive('about') ? 'active' : ''}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/about');
              }}
            >
              About Us
            </a>
          </li>
          <li>
            <a
              href='/products'
              className={isActive('products') ? 'active' : ''}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/products');
              }}
            >
              Products
            </a>
          </li>
          <li>
            <a
              href='/service'
              className={isActive('service') ? 'active' : ''}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/service');
              }}
            >
              Service
            </a>
          </li>
          <li>
            <a
              href='/job'
              className={isActive('job') ? 'active' : ''}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/job');
              }}
            >
              Job
            </a>
          </li>

          {/* All Products Dropdown */}
          <li className='dropdown-trigger'>
            <a
              href='#'
              onClick={(e) => {
                e.preventDefault();
                handleDropdownToggle('products', e);
              }}
            >
              All Products ▾
            </a>
            <ul
              className={`dropdown-menu ${
                activeDropdown === 'products' ? 'active' : ''
              }`}
            >
              <li>
                <a
                  href='/products?category=electronics'
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigation('/products?category=electronics');
                  }}
                >
                  Electronics
                </a>
              </li>
              <li>
                <a
                  href='/products?category=clothing'
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigation('/products?category=clothing');
                  }}
                >
                  Clothing
                </a>
              </li>
              <li>
                <a
                  href='/products?category=home-garden'
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigation('/products?category=home-garden');
                  }}
                >
                  Home & Garden
                </a>
              </li>
              <li>
                <a
                  href='/products?category=beauty-health'
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigation('/products?category=beauty-health');
                  }}
                >
                  Beauty & Health
                </a>
              </li>
              <li>
                <a
                  href='/products?category=sports-outdoors'
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigation('/products?category=sports-outdoors');
                  }}
                >
                  Sports & Outdoors
                </a>
              </li>
              <li>
                <a
                  href='/products?category=books-media'
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigation('/products?category=books-media');
                  }}
                >
                  Books & Media
                </a>
              </li>
            </ul>
          </li>

          <li>
            <a
              href='/contact'
              className={isActive('contact') ? 'active' : ''}
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/contact');
              }}
            >
              Contact Us
            </a>
          </li>

          {/* Sri Lanka Dropdown */}
          <li className='dropdown-trigger'>
            <a
              href='#'
              onClick={(e) => {
                e.preventDefault();
                handleDropdownToggle('sri-lanka', e);
              }}
            >
              Sri Lanka ▾
            </a>
            <ul
              className={`dropdown-menu ${
                activeDropdown === 'sri-lanka' ? 'active' : ''
              }`}
            >
              {[
                'colombo',
                'kandy',
                'galle',
                'ampara',
                'anuradhapura',
                'badulla',
                'batticaloa',
                'chilaw',
                'dambulla',
                'gampaha',
                'hambantota',
                'jaffna',
                'kalmunai',
                'kalutara',
                'kegalle',
                'kurunegala',
                'mannar',
                'matale',
                'matara',
                'monaragala',
                'mullaitivu',
                'negombo',
                'nuwaraeliya',
                'polonnaruwa',
                'puttalam',
                'ratnapura',
                'trincomalee',
                'vavuniya',
              ].map((city) => (
                <li key={city}>
                  <a
                    href='#'
                    onClick={(e) => {
                      e.preventDefault();
                      handleLanguageChange(city);
                    }}
                  >
                    {city.charAt(0).toUpperCase() + city.slice(1)}
                  </a>
                </li>
              ))}
            </ul>
          </li>
        </ul>

        {/* Search Bar - Conditionally rendered based on route */}
        {shouldShowSearchBar() && (
          <div
            className={`search-container ${
              isMobileMenuOpen ? 'mobile-visible' : ''
            }`}
          >
            <form className='search-form' onSubmit={handleSearch}>
              <input
                type='text'
                placeholder='Search products...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='search-input'
              />
              <button type='submit' className='search-button'>
                <FiSearch />
              </button>
            </form>
          </div>
        )}

        {/* Icons Section */}
        <div
          className={`icons-section ${
            isMobileMenuOpen ? 'mobile-visible' : ''
          }`}
        >
          <button className='icon-btn'>
            <FiHeart />
            <span className='icon-badge'>0</span>
          </button>
          <button
            className='icon-btn'
            onClick={() => handleNavigation('/cart')}
          >
            <FiShoppingCart />
            <span className='icon-badge'>0</span>
          </button>

          {/* User Profile Dropdown */}
          <div className='user-dropdown-trigger'>
            <button
              className='icon-btn'
              onClick={(e) => {
                e.stopPropagation();
                handleDropdownToggle('user', e);
              }}
            >
              <FiUser />
            </button>
            <ul
              className={`user-dropdown-menu ${
                activeDropdown === 'user' ? 'active' : ''
              }`}
            >
              {!isLoggedIn ? (
                <>
                  <li>
                    <button className='dropdown-item' onClick={handleLogin}>
                      <FiUser className='dropdown-icon' />
                      Login
                    </button>
                  </li>
                  <li>
                    <button className='dropdown-item' onClick={handleSignup}>
                      <FiUser className='dropdown-icon' />
                      Sign Up
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <button
                      className='dropdown-item'
                      onClick={() => handleProfileNavigation('/profile')}
                    >
                      <FiUser className='dropdown-icon' />
                      Profile
                    </button>
                  </li>
                  <li>
                    <button
                      className='dropdown-item'
                      onClick={() => handleProfileNavigation('/orders')}
                    >
                      <FiShoppingBag className='dropdown-icon' />
                      Orders
                    </button>
                  </li>
                  <li>
                    <button className='dropdown-item' onClick={handleLogout}>
                      <FiLogOut className='dropdown-icon' />
                      Logout
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
